import { Mail, ShieldCheck } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";

export interface SendCardDataEmailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recipientEmail: string;
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function SendCardDataEmailModal({
  open,
  onOpenChange,
  recipientEmail,
  isLoading = false,
  onConfirm,
  onCancel,
}: SendCardDataEmailModalProps) {
  const { t } = useTranslations("cards");

  return (
    <ColocaDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
        onOpenChange(next);
      }}
      size="default"
      title={
        <span className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-primary" aria-hidden="true" />
          {t("card_detail_email_modal_title")}
        </span>
      }
      description={t("card_detail_email_modal_description")}
      footer={
        <>
          <ColocaButton variant="ghost" onClick={onCancel} disabled={isLoading}>
            {t("card_detail_email_button_cancel")}
          </ColocaButton>
          <ColocaButton
            variant="primary"
            onClick={onConfirm}
            loading={isLoading}
            disabled={isLoading || !recipientEmail}
          >
            {t("card_detail_email_button_confirm")}
          </ColocaButton>
        </>
      }
    >
      <div className="space-y-4">
        <ColocaAlert
          variant="warning"
          icon={ShieldCheck}
          heading={t("card_detail_email_modal_security_note")}
        />
        <p className="text-sm text-muted-foreground">{t("card_detail_email_modal_body")}</p>
        <p className="rounded-md border border-border bg-muted/30 px-3 py-2 font-mono text-sm break-all text-foreground">
          {recipientEmail || "—"}
        </p>
      </div>
    </ColocaDialog>
  );
}
